// Entity factory helpers. Building an ally / enemy / projectile is enough
// boilerplate that it's worth centralising; systems don't manipulate stores
// directly when adding entities.

import type { WorldState } from './world-state';
import { C } from './components';
import type {
  Position,
  Velocity,
  Radius,
  Health,
  AttackComp,
  FactionComp,
  Age,
  GenomeComp,
  Stats,
  EmergentComp,
  RenderInfo,
  Fertility,
  Projectile,
  PickupComp,
  PhoenixComp,
  PulseTimerComp,
  XpComp,
} from './components';
import { randomGenome, type Genome } from './genome';
import { resolveStats } from './traits/base';
import { evaluateEmergent, mergeEffects } from './traits/emergent';
import { randFloat } from './rng';

/** Map a genome to a visible color for placeholder rendering. */
function colorForGenome(g: Genome): number {
  // Pull three "hue" alleles deterministically.
  const r = Math.floor(((g[0] ?? 8) / 15) * 200) + 40;
  const gr = Math.floor(((g[12] ?? 8) / 15) * 200) + 40;
  const b = Math.floor(((g[14] ?? 8) / 15) * 200) + 40;
  // eslint-disable-next-line no-bitwise
  return (r << 16) | (gr << 8) | b;
}

export interface SpawnAllyOpts {
  genome: Genome;
  x: number;
  y: number;
}

export function spawnAlly(ws: WorldState, opts: SpawnAllyOpts): number {
  const { world } = ws;
  const id = world.createEntity();
  const stats = resolveStats(opts.genome);
  const emergent = evaluateEmergent(opts.genome);

  world.store<Position>(C.position).set(id, { x: opts.x, y: opts.y });
  world.store<Velocity>(C.velocity).set(id, { vx: 0, vy: 0 });
  world.store<Radius>(C.radius).set(id, { r: 6 * stats.size });
  world.store<Health>(C.health).set(id, { current: stats.maxHealth, max: stats.maxHealth });
  world.store<Age>(C.age).set(id, { seconds: 0, lifespan: stats.lifespan });
  world.store<Fertility>(C.fertility).set(id, {
    start: clamp01(stats.fertilityWindowStart),
    end: clamp01(stats.fertilityWindowEnd),
  });
  world.store<AttackComp>(C.attack).set(id, {
    range: stats.attackRange,
    rate: stats.attackRate,
    damage: stats.attackDamage,
    cooldown: 0,
    projectileSpeed: stats.projectileSpeed,
  });
  world.store<FactionComp>(C.faction).set(id, { side: 'ally' });
  world.store<GenomeComp>(C.genome).set(id, { genome: opts.genome });
  world.store<Stats>(C.stats).set(id, { sheet: stats });
  const mergedEffect = mergeEffects(emergent);
  world.store<EmergentComp>(C.emergent).set(id, {
    effect: mergedEffect,
    ids: emergent.map((e) => e.id),
  });
  world.store<RenderInfo>(C.render).set(id, {
    color: colorForGenome(opts.genome),
    outline: 0xffffff,
  });
  // Phoenix: one-shot resurrection state, attached only when the emergent fires.
  if (mergedEffect.phoenix) {
    world.store<PhoenixComp>(C.phoenix).set(id, { used: false });
  }
  // Pulsar: periodic AOE damage timer.
  if (mergedEffect.pulse) {
    world.store<PulseTimerComp>(C.pulseTimer).set(id, {
      remaining: mergedEffect.pulse.intervalSec,
      intervalSec: mergedEffect.pulse.intervalSec,
      radius: mergedEffect.pulse.radius,
      damage: mergedEffect.pulse.damage,
    });
  }
  // XP / leveling progression.
  world.store<XpComp>(C.xp).set(id, { xp: 0, level: 0 });
  return id;
}

/** Spawn N allies in a circle around (cx, cy) for the initial flock. */
export function spawnInitialFlock(ws: WorldState, count: number, cx: number, cy: number): number[] {
  const ids: number[] = [];
  const rng = ws.rng('spawn');
  const genomeLen = 25; // 21 base traits + 4 hidden
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const radius = 60 + randFloat(rng, 0, 40);
    const id = spawnAlly(ws, {
      genome: randomGenome(rng, genomeLen),
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    });
    ids.push(id);
  }
  return ids;
}

// Enemy archetype defs + spawn helpers moved to ./enemies.ts (kept here to
// keep import sites stable for SEEKER/spawnEnemy until they're updated).
export { ARCHETYPES, spawnEnemyOf } from './enemies';

export interface SpawnProjectileOpts {
  ownerId: number;
  side: 'ally' | 'enemy';
  x: number;
  y: number;
  vx: number;
  vy: number;
  damage: number;
  pierce: number;
  /** Optional frostbitten effect to apply per-hit. */
  freeze?: { chance: number; duration: number };
}

export function spawnProjectile(ws: WorldState, opts: SpawnProjectileOpts): number {
  const { world } = ws;
  const id = world.createEntity();
  world.store<Position>(C.position).set(id, { x: opts.x, y: opts.y });
  world.store<Velocity>(C.velocity).set(id, { vx: opts.vx, vy: opts.vy });
  world.store<Radius>(C.radius).set(id, { r: 2 });
  const projectile: Projectile = {
    ownerId: opts.ownerId,
    side: opts.side,
    damage: opts.damage,
    pierce: opts.pierce,
    ttl: 1.5,
    hits: new Set(),
  };
  if (opts.freeze) projectile.freeze = opts.freeze;
  world.store<Projectile>(C.projectile).set(id, projectile);
  world.store<RenderInfo>(C.render).set(id, {
    color: opts.side === 'ally' ? 0xffe066 : 0xff66aa,
    outline: 0x000000,
  });
  return id;
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

export function spawnPickup(ws: WorldState, x: number, y: number): number {
  const { world } = ws;
  const id = world.createEntity();
  world.store<Position>(C.position).set(id, { x, y });
  world.store<Velocity>(C.velocity).set(id, { vx: 0, vy: 0 });
  world.store<Radius>(C.radius).set(id, { r: 6 });
  world.store<PickupComp>(C.pickup).set(id, { kind: 'breed', ttl: 30 });
  world.store<RenderInfo>(C.render).set(id, { color: 0xff9ec4, outline: 0xffffff });
  return id;
}
